import React, { useCallback, useEffect, useState } from 'react';
import { Heart } from 'lucide-react';
import Block, { LINK_BUTTON } from './Block';
import { apiService } from '../../services/api';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useToast } from '../../contexts/ToastContext';
import { GivenKudos } from './types';
import { emojiOf } from './kudos';
import * as fmt from '../../lib/format';

/** How many of the kudos they gave show here. */
export const GIVEN_SHOWN = 3;

// For a leader or an admin: thank a team or a person, and see the last few kudos you gave, with a way to take
// one back. There is no count and no ranking: nothing here is added up.
export default function KudosBlock({ onGive, reloadKey, className = '' }: { onGive: () => void; /** Changes when a kudos was sent, so the list is read again. */ reloadKey: number; className?: string }) {
  const confirm = useConfirm();
  const { showToast } = useToast();
  const [given, setGiven] = useState<GivenKudos[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await apiService.getKudosGiven(GIVEN_SHOWN);
      if (!r.success) throw new Error(r.error || 'Could not load your kudos');
      setGiven(r.data ?? []);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Could not load your kudos');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const takeBack = async (k: GivenKudos) => {
    const yes = await confirm({
      title: `Take back your kudos to ${k.to_name}?`,
      message: 'They will no longer see it in their Updates.',
      confirmLabel: 'Take back kudos',
    });
    if (!yes) return;
    try {
      const r = await apiService.deleteKudos(k.id);
      if (!r.success) throw new Error(r.error || 'Could not take the kudos back');
      showToast('success', 'Took the kudos back');
      await load();
    } catch (e: any) {
      showToast('error', e?.response?.data?.error || e?.message || 'Could not take the kudos back');
    }
  };

  return (
    <Block title="Kudos" className={className}>
      <p className="text-sm text-gray-700">Thank a team or a person. They see it in their Updates, and so do the people on the project you name.</p>
      <button type="button" onClick={onGive} className={LINK_BUTTON}>
        <Heart className="h-4 w-4" aria-hidden />
        Give kudos
      </button>

      {error && (
        <p role="alert" className="mt-3 text-sm text-rose-700">
          {error}{' '}
          <button type="button" onClick={load} className="font-medium underline">
            Try again
          </button>
        </p>
      )}

      {given && given.length > 0 && (
        <div className="mt-4">
          <p className="text-sm font-medium text-gray-900">You gave lately</p>
          <ul className="mt-1 divide-y divide-gray-100">
            {given.map((k) => (
              <li key={k.id} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2.5">
                <p className="min-w-0 text-sm text-gray-800">
                  <span role="img" aria-label={emojiOf(k.emoji)?.label ?? 'Kudos'} className="mr-1.5">
                    {emojiOf(k.emoji)?.glyph ?? '🙂'}
                  </span>
                  To {k.to_name}
                  {k.project_key ? ` for ${k.project_key}` : ''} on {fmt.date(k.created_at)}
                  {k.note ? `: “${k.note}”` : '.'}
                </p>
                <button
                  type="button"
                  onClick={() => takeBack(k)}
                  aria-label={`Take back your kudos to ${k.to_name}`}
                  className="rounded px-1 py-1 text-xs font-medium text-gray-700 underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                >
                  Take back
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

    </Block>
  );
}
